import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Folder, File, ArrowLeft, Info, ChevronLeft, ChevronRight } from 'lucide-react';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Alert } from "@/components/ui/alert";

import type { FileSystemItem, FileBrowserDialogProps } from '@/types/file-browser';
import { useToolContext } from '@/contexts/ToolContext';
import { useAssistant } from '@/contexts/AssistantContext';
import FirebaseService from '@/lib/firebase';
import { format } from 'date-fns';
import { SUPPORTED_EXTENSIONS } from '@/lib/supported-file-types';

interface GCSObject {
    name: string;
    size?: string | number;
    contentType?: string;
    updated?: string;
    created?: string;
    id?: string;
    timeCreated?: string;
    timeStorageClassUpdated?: string;
    crc32c?: string;
    md5Hash?: string;
}

// New pagination interface
interface PaginationState {
    pageSize: number;
    pageToken?: string;
    hasMore: boolean;
    currentPage: number;
}

export function FileBrowserDialog({
  open,
  onOpenChange,
  onSelect,
  selectedItems = []
}: FileBrowserDialogProps) {
  const { toolConfigs } = useToolContext();
  const assistantContext = useAssistant();

  // Try document_search_agent first, then fall back to file-browser
  const documentAgentConfig = toolConfigs['document_search_agent'] || {};
  const fileConfig = toolConfigs['file-browser'] || {};

  // Both tools now use 'bucketUrl' consistently
  const bucketUrl = documentAgentConfig.bucketUrl || fileConfig.bucketUrl;
  const rootPath = fileConfig.rootPath || '/';
  
  // Determine which tool is providing the configuration
  const activeToolName = documentAgentConfig.bucketUrl ? 'Document Search Agent' : 'File Browser';

  const [currentPath, setCurrentPath] = useState(rootPath);
  const [pathHistory, setPathHistory] = useState<string[]>([rootPath]);
  const [fileSystem, setFileSystem] = useState<GCSObject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    pageSize: 50,
    hasMore: false,
    currentPage: 1
  });
  
  // Token history to support navigation
  const [pageTokenHistory, setPageTokenHistory] = useState<Map<number, string>>(new Map());

  const loadFolderContents = useCallback(async (path: string, pageToken?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      let normalizedPath = path;
      if (normalizedPath.startsWith('/')) {
        normalizedPath = normalizedPath.slice(1);
      }

      const response = await fetch('/api/gcs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bucket: bucketUrl,
          prefix: normalizedPath === '/' ? '' : normalizedPath,
          delimiter: '/',
          maxResults: pagination.pageSize,
          pageToken: pageToken
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load storage contents');
      }

      const data = await response.json();
      setFileSystem(data.contents);
      
      // Update pagination state
      setPagination(prev => ({
        ...prev,
        pageToken: data.nextPageToken,
        hasMore: !!data.nextPageToken
      }));
      
      // Store the next page token if it exists
      if (data.nextPageToken) {
        setPageTokenHistory(prev => {
          const newMap = new Map(prev);
          newMap.set(pagination.currentPage + 1, data.nextPageToken);
          return newMap;
        });
      }
    } catch (err) {
      console.error('Error loading folder contents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load storage contents');
    } finally {
      setIsLoading(false);
    }
  }, [bucketUrl, pagination.pageSize, pagination.currentPage]);

  useEffect(() => {
    if (open && bucketUrl) {
      // Reset pagination whenever the dialog opens or path changes
      setPagination({
        pageSize: 50,
        hasMore: false,
        currentPage: 1
      });
      setPageTokenHistory(new Map());
      loadFolderContents(currentPath);
    }
  }, [open, bucketUrl, currentPath, loadFolderContents]);

  const navigateToFolder = (folderPath: string) => {
    setPathHistory(prev => [...prev, currentPath]);
    setCurrentPath(folderPath);
  };

  const navigateUp = () => {
    const previousPath = pathHistory[pathHistory.length - 1];
    if (previousPath) {
      setPathHistory(prev => prev.slice(0, -1));
      setCurrentPath(previousPath);
    }
  };

  const goToNextPage = () => {
    if (pagination.hasMore) {
      const nextPage = pagination.currentPage + 1;
      setPagination(prev => ({
        ...prev,
        currentPage: nextPage
      }));
      
      // Use the stored token for the next page
      const nextPageToken = pageTokenHistory.get(nextPage);
      loadFolderContents(currentPath, nextPageToken);
    }
  };

  const goToPreviousPage = () => {
    if (pagination.currentPage > 1) {
      const prevPage = pagination.currentPage - 1;
      setPagination(prev => ({
        ...prev,
        currentPage: prevPage
      }));
      
      // For previous page, use the token for page - 1 or undefined for first page
      const prevPageToken = prevPage > 1 ? pageTokenHistory.get(prevPage - 1) : undefined;
      loadFolderContents(currentPath, prevPageToken);
    }
  };

  const isItemSelected = (path: string, type: 'file' | 'folder'): boolean => {
    return selectedItems.some(item => item.path === path && item.type === type);
  };

  const toggleSelection = (path: string, name: string, type: 'file' | 'folder', e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation(); // Prevent folder navigation when clicking checkbox
    }

    const isSelected = isItemSelected(path, type);
    if (isSelected) {
      onSelect(selectedItems.filter(item => !(item.path === path && item.type === type)));
    } else {
      onSelect([...selectedItems, { path, type, name }]);
    }
  };

  // Track file selections in file history
  const trackFileSelections = async () => {
    console.log('🔍 trackFileSelections called, selectedItems:', selectedItems);
    const user = FirebaseService.auth.currentUser;
    if (!user) {
      console.log('❌ No user logged in, skipping tracking');
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const assistantId = assistantContext?.config?.assistantId || 'unknown';
      const assistantName = assistantContext?.config?.name || 'Unknown Assistant';
      const assistantAvatar = assistantContext?.config?.avatar || null;

      console.log('📋 Assistant context:', { assistantId, assistantName, assistantAvatar });

      // Track each selected file (not folders)
      const fileItems = selectedItems.filter(item => item.type === 'file');
      console.log(`📁 Found ${fileItems.length} file items to track`);

      for (const item of fileItems) {
        try {
          // Construct full GCS URI - bucketUrl already has gs:// prefix
          const cleanBucket = bucketUrl.startsWith('gs://') ? bucketUrl : `gs://${bucketUrl}`;
          const fullPath = `${cleanBucket}/${item.path}`;
          console.log(`📤 Tracking file: ${fullPath}`);

          const response = await fetch('/api/user/previews', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              sourceUri: fullPath,
              fileName: item.name,
              fileType: 'application/octet-stream', // Default, could infer from extension
              activityType: 'selection',
              assistantId,
              assistantName,
              assistantAvatar,
              conversationUrl: `/assistant/${assistantId}`,
              copySucceeded: false // Browser selections aren't copied
            })
          });

          if (!response.ok) {
            console.error(`❌ API returned ${response.status}:`, await response.text());
          } else {
            console.log(`✅ Tracked: ${item.name}`);
          }
        } catch (error) {
          console.error('Failed to track file selection:', item.name, error);
          // Continue tracking other files even if one fails
        }
      }

      console.log(`✅ Tracked ${fileItems.length} file selections`);
    } catch (error) {
      console.error('Failed to track file selections:', error);
    }
  };

  function formatDate(dateString?: string): string {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'MMM d, yyyy HH:mm:ss');
    } catch {
      return dateString;
    }
  }

  // Group files and folders
  const groupedContents = React.useMemo(() => {
    const folders: GCSObject[] = [];
    const files: GCSObject[] = [];

    fileSystem.forEach(item => {
      if (item.name.endsWith('/')) {
        folders.push(item);
      } else {
        files.push(item);
      }
    });

    return { folders, files };
  }, [fileSystem]);

  if (!bucketUrl) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>File Browser Configuration Required</DialogTitle>
          </DialogHeader>
          <Alert>
            <span>Please configure the file browser or document search agent in the admin settings first.</span>
          </Alert>
          <div className="text-sm text-muted-foreground">
            Configuration required for File Browser:
            <ul className="list-disc ml-4 mt-2">
              <li>Storage Bucket URL</li>
              <li>Root Path (optional)</li>
            </ul>
            Or configure Document Search Agent with preset configurations.
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <TooltipProvider>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>Select Files or Folders</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-sm">
                    <div className="space-y-1">
                      <p>Only supported file types are shown: {SUPPORTED_EXTENSIONS.slice(0, 8).join(', ')}
                        {SUPPORTED_EXTENSIONS.length > 8 && ` and ${SUPPORTED_EXTENSIONS.length - 8} more`}</p>
                      <p>When selecting a folder, only up to 5 files will be read by the assistant.</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-normal text-muted-foreground truncate max-w-xs">
                {bucketUrl}
              </span>
            </DialogTitle>
          </DialogHeader>

          {/* Current path navigation */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground flex-shrink-0">
            {currentPath !== rootPath && (
              <Button
                variant="ghost"
                size="sm"
                onClick={navigateUp}
                className="h-8 px-2"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <div className="flex-1 truncate">
              Current path: {currentPath}
            </div>
          </div>

          {/* Selected Items */}
          {selectedItems.length > 0 && (
            <div className="flex flex-wrap gap-1 p-2 bg-gray-50 rounded flex-shrink-0">
              {selectedItems.map(item => (
                <Badge 
                  key={`${item.type}-${item.path}`}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  {item.type === 'folder' ? (
                    <Folder className="h-3 w-3" />
                  ) : (
                    <File className="h-3 w-3" />
                  )}
                  {item.name}
                  <span 
                    className="ml-1 cursor-pointer hover:text-gray-700"
                    onClick={() => toggleSelection(item.path, item.name, item.type)}
                  >
                    ×
                  </span>
                </Badge>
              ))}
            </div>
          )}

          <div className="flex-1 min-h-0">
            <ScrollArea className="h-[60vh] border rounded-lg">
              <div className="p-4">
                {isLoading ? (
                  <div className="flex items-center justify-center h-32">
                    Loading...
                  </div>
                ) : error ? (
                  <div className="text-red-500 p-4 text-center">
                    {error}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {/* Folders */}
                    {groupedContents.folders.map((folder) => {
                      const name = folder.name.split('/').slice(-2)[0];
                      const isSelected = isItemSelected(folder.name, 'folder');
                      
                      return (
                        <div 
                          key={folder.name}
                          className="flex items-center p-2 rounded hover:bg-gray-100"
                          onClick={() => navigateToFolder(folder.name)}
                        >
                          <div className="flex items-center mr-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => toggleSelection(folder.name, name, 'folder')}
                              className="w-4 h-4 rounded border-gray-300"
                            />
                          </div>
                          <Folder className="h-4 w-4 mr-2 text-blue-500" />
                          <span className="truncate flex-1">{name}</span>
                        </div>
                      );
                    })}
                    
                    {/* Files */}
                    {groupedContents.files.map((file) => {
                        const name = file.name.split('/').pop() || '';
                        const isSelected = isItemSelected(file.name, 'file');
                      
                      return (
                        <div 
                          key={file.name}
                          className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-100 ${
                            isSelected ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => toggleSelection(file.name, name, 'file')}
                        >
                          <File className="h-4 w-4 mr-2 text-gray-500" />
                          <span className="truncate flex-1">{name}</span>
                          <span className="text-xs text-gray-500">
                            {file.size && formatFileSize(Number(file.size))}
                          </span>
                        </div>
                      );
                    })}

                    {groupedContents.folders.length === 0 && 
                     groupedContents.files.length === 0 && (
                      <div className="text-center text-gray-500 py-8">
                        This folder is empty
                      </div>
                    )}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Bottom section with pagination and buttons - always visible */}
          <div className="flex-shrink-0 space-y-2 border-t pt-2">
            {/* Pagination UI */}
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Page {pagination.currentPage} {pagination.hasMore ? '(more available)' : ''}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPreviousPage}
                  disabled={pagination.currentPage <= 1 || isLoading}
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">Previous</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNextPage}
                  disabled={!pagination.hasMore || isLoading}
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  await trackFileSelections();
                  onOpenChange(false);
                }}
                disabled={selectedItems.length === 0}
              >
                Use Selected Items
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}